/*!
 * Copyright © 2019-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { PendoService } from '@fleetmatics/ui.insights';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { EMPTY, forkJoin, merge, Observable, of, partition } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  delay,
  filter,
  map,
  share,
  skip,
  switchMap,
  take,
  tap,
  throttleTime,
  withLatestFrom
} from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { EpochTimeUtilities, isArrayNullOrEmpty, isElementNull, isStringNullOrEmpty, MomentUtilities } from '../../../../utils';
import {
  EPlotType,
  ETrackingDeviceEventCode,
  EViewReplayType,
  IAssetPlot,
  IDriverDetails,
  IIndexableObject,
  IReplayLayout,
  IReplayPlot,
  IReplayUserSettings,
  IReverseGeocodeRequest,
  IReverseGeocodeResponse,
  IUserSettings,
  IVehiclePlot,
  IVehiclePlotResponse,
  IVideoEvent,
  LatLng
} from '../../../core/models';
import { delayedRetry } from '../../../core/operators/delayed-retry.operator';
import {
  AddressResolutionHttpService,
  AssetsHttpService,
  CustomerMetadataHttpService,
  VideoEventHttpService,
  WindowResizeService
} from '../../../core/services';
import {
  ChangeMapBounds,
  CloseBalloon,
  EBalloonInfoActions,
  ELayoutActions,
  EMapsActions,
  EVehicleActions,
  GetMapHierarchy,
  GetPlaceBalloonSuccess,
  GetPolylineBalloon,
  GetReplayBalloon,
  GetReplayBalloonSuccess,
  GetReplayUserSettings,
  GetVideoDeviceInfo,
  OpenReplayOnLiveMap,
  PolylineClicked,
  PolylineEndHover,
  PolylineStartHover,
  UpdateIsMapFitted,
  UpdateIsStreetViewOpen,
  UpdateVehicleAsSelected,
  UpdateVehicles,
  UpdateViewReplayIdAndType
} from '../../../core/store/actions';
import {
  getAssetDetails,
  getAssetPlots,
  getBalloon,
  getIsStreetViewOpen,
  getReplayLayout,
  getReplayUserSettings,
  getShowHarshDrivingEvents,
  getShowReplayVideoEnabled,
  getShowSpeedingEvents,
  getUserSettings,
  getUserSpeedingThreshold,
  getVehiclePlots
} from '../../../core/store/selectors';
import { IAppState } from '../../../core/store/state';
import {
  DateRange,
  EGPSQuality,
  EPanelDrawerState,
  IAssetLocationHisoryRequestDto,
  IAssetLocationHistoryResponseDto,
  IAssetLocationsHistory,
  IGetJourneyForVehicleRequest,
  IGetReplayJourneyParams,
  IGetReplayPlotsRequest,
  IGetVehicleJourneyResponse,
  IGetVehicleReplayPlotsResponse,
  IReplayJourney,
  Segment,
  TGetReplayData,
  Vehicle
} from '../../models';
import { ReplayHttpService } from '../../services';
import {
  AddLivePlotToJourney,
  AddLivePlotToJourneySuccess,
  ChangeDateRangeOfJourney,
  ClearReplayDataWhileFetching,
  DeselectDayFromMultidayPanel,
  DeselectReplaySegment,
  EndReplayPlotAnimation,
  EReplayActions,
  FitToMap,
  GetJourneyUpdates,
  GetJourneyUpdatesSuccess,
  GetNewReplayJourney,
  GetReplayJourney,
  GetReplayJourneyError,
  GetReplayJourneySuccess,
  NextPlotInJourney,
  PanelScrolled,
  PreviousPlotInJourney,
  RequestVideoInJourney,
  RequestVideoInJourneySuccess,
  SegmentVideoClicked,
  SelectDayFromMultidayPanel,
  SelectReplaySegmentFromMap,
  SelectReplaySegmentFromTimeline,
  StartReplayPlotAnimation,
  UpdateCanFitToMap,
  UpdateMobileDrawerState,
  VerifyRecentActivityDate,
  VerifyRecentActivityDateError,
  VerifyRecentActivityDateSuccess,
  ViewAll
} from '../actions';
import {
  canFitToMap,
  getIsReplaySettingsLoaded,
  getIsSelectedSegmentSticky,
  getPlotsOfSelectedSegment,
  getReplayJourney,
  getReplayPlots,
  getSegmentOfPlot
} from '../selectors/replay.selectors';
import { ReplayMappingUtilities } from './replay-mapping.utilities';

const GetJourneyUpdatesDelayMs = 10000;
const ChangeDateRangeDebounceTimeMs = 500;

@Injectable()
export class ReplayEffects {
  private readonly _driverNamesMap = new Map<number, IDriverDetails>();

  getReplayUserSettingsIfEmpty$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayJourney>(EReplayActions.GetReplayJourney),
      filter(() => isPlatformBrowser(this._platformId)),
      switchMap(() => this._store.pipe(select(getReplayUserSettings))),
      filter(replayUserSettings => isElementNull(replayUserSettings)),
      map(() => new GetReplayUserSettings())
    )
  );

  getReplayUserSpeedingThreshholdIfEmpty$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayJourney>(EReplayActions.GetReplayJourney),
      filter(() => isPlatformBrowser(this._platformId)),
      switchMap(() => this._store.pipe(select(getUserSpeedingThreshold))),
      filter(userSpeedingThreshold => isElementNull(userSpeedingThreshold)),
      map(() => new GetMapHierarchy({ refreshPlots: false }))
    )
  );

  onDateChangeGetJourneyForVehicle$ = createEffect(() =>
    this._actions$.pipe(
      ofType<ChangeDateRangeOfJourney>(EReplayActions.ChangeDateRangeOfJourney),
      filter(() => isPlatformBrowser(this._platformId)),
      debounceTime(ChangeDateRangeDebounceTimeMs),
      withLatestFrom(this._store.pipe(select(getReplayLayout)), this._store.pipe(select(getReplayJourney))),
      map(([, replayLayout, journey]) => {
        const journeyParams: IGetReplayJourneyParams = {
          id: journey.id,
          type: replayLayout.viewReplayParams.type,
          dateRange: journey.selectedDateRange,
          accountSettingOverrides: replayLayout.viewReplayParams.accountSettingOverrides,
          shouldQueryExtraDay: true
        };
        return new GetReplayJourney(journeyParams);
      })
    )
  );

  clearReplayPanelWhileFetchingReplayData$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayJourney>(EReplayActions.GetReplayJourney),
      filter(() => isPlatformBrowser(this._platformId)),
      map(action => action.payload),
      withLatestFrom(
        this._store.pipe(select(getReplayJourney)),
        this._store.pipe(select(getVehiclePlots)),
        this._store.pipe(select(getAssetPlots)),
        this._store.pipe(select(getUserSettings))
      ),
      map(([request, journey, vehiclePlots, assetPlots, userSettings]) => {
        const name = this._getTrackableName(vehiclePlots, assetPlots, request, journey);
        const adjustedDates = this._adjustParamsToUserTimezone(request, userSettings);

        return ReplayMappingUtilities.getEmptyJourney(request.id, adjustedDates.dateRange, name, request.type);
      }),
      switchMap(journey => [new ClearReplayDataWhileFetching(journey), new UpdateIsStreetViewOpen(false)])
    )
  );

  getReplayJourney$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayJourney>(EReplayActions.GetReplayJourney),
      filter(() => isPlatformBrowser(this._platformId)),
      switchMap(action =>
        // wait until replay settings are loaded
        this._store.pipe(
          select(getIsReplaySettingsLoaded),
          filter(isLoaded => isLoaded),
          take(1),
          map(() => action)
        )
      ),
      map(action => action.payload),
      withLatestFrom<
        IGetReplayJourneyParams,
        [IGetReplayJourneyParams, IReplayUserSettings, number, IUserSettings, boolean, boolean, boolean, boolean]
      >(
        this._store.pipe(select(getReplayUserSettings)),
        this._store.pipe(select(getUserSpeedingThreshold)),
        this._store.pipe(select(getUserSettings)),
        this._store.pipe(select(getShowSpeedingEvents)),
        this._store.pipe(select(getShowHarshDrivingEvents)),
        this._store.pipe(select(getShowReplayVideoEnabled))
      ),
      switchMap(
        ([
          requestParams,
          replayUserSettings,
          userSpeedingThreshold,
          userSettings,
          showSpeedingEvents,
          showHarshDrivingEvents,
          showVideoEvents
        ]) =>
          requestParams.type === EViewReplayType.Vehicle
            ? this._handleVehicleReplayData(
                requestParams,
                replayUserSettings,
                userSpeedingThreshold,
                userSettings,
                showSpeedingEvents,
                showHarshDrivingEvents,
                showVideoEvents
              )
            : this._handleAssetReplayData(requestParams, userSettings)
      )
    )
  );

  public onReplaySelectUpdateMapBounds$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SelectReplaySegmentFromTimeline>(EReplayActions.SelectReplaySegmentFromTimeline),
      withLatestFrom(this._store.pipe(select(getPlotsOfSelectedSegment))),
      filter(([, plots]) => !isElementNull(plots) && plots.length > 0),
      map(([, plots]) => plots.map(p => p.location)),
      map(coordinates => coordinates.map(coordinate => LatLng.fromCoordinate(coordinate))),
      map(latLngs => new ChangeMapBounds(latLngs))
    )
  );

  public onReplaySelectTryAndCloseStreetView$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SelectReplaySegmentFromTimeline | ViewAll>(EReplayActions.SelectReplaySegmentFromTimeline, EReplayActions.ViewAll),
      withLatestFrom(this._store.pipe(select(getIsStreetViewOpen))),
      filter(([, isStreetViewOpen]) => isStreetViewOpen),
      map(() => new UpdateIsStreetViewOpen(false))
    )
  );

  public fitToMap$ = createEffect(() =>
    this._actions$.pipe(
      ofType<FitToMap>(EReplayActions.FitToMap),
      switchMap(() => this._store.pipe(select(getReplayPlots), take(1))),
      map(plots => plots.map(p => p.center)),
      map(coordinates => coordinates.map(coordinate => LatLng.fromCoordinate(coordinate))),
      map(latLngs => new ChangeMapBounds(latLngs))
    )
  );

  public dispatchFitToMap$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayJourneySuccess | ViewAll>(EReplayActions.GetReplayJourneySuccess, EReplayActions.ViewAll),
      map(() => new FitToMap())
    )
  );

  public retrieveVideoDeviceInfo$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayJourneySuccess>(EReplayActions.GetReplayJourneySuccess),
      filter(
        action =>
          action.payload.type === EViewReplayType.Vehicle &&
          action.payload.days.some(day => day.segments.some(segment => segment.plots.length > 0))
      ),
      switchMap(async action => new GetVideoDeviceInfo({ vehicleId: action.payload.id }))
    )
  );

  public updateCanFitToMap$ = createEffect(() =>
    this._actions$.pipe(
      ofType<FitToMap | GetReplayJourneySuccess>(EReplayActions.FitToMap, EReplayActions.GetReplayJourneySuccess),
      switchMap(() =>
        this._actions$.pipe(
          ofType<UpdateIsMapFitted>(ELayoutActions.UpdateIsMapFitted),
          map(action => action.payload),
          filter(isMapFitted => !isMapFitted),
          skip(1),
          take(1)
        )
      ),
      map(() => new UpdateCanFitToMap(true))
    )
  );

  public onSelectDayFromMultidayPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SelectDayFromMultidayPanel>(EReplayActions.SelectDayFromMultidayPanel),
      switchMap(() => [new EndReplayPlotAnimation(), new StartReplayPlotAnimation(), new ViewAll()])
    )
  );

  public onDeselectDayFromMultidayPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<DeselectDayFromMultidayPanel>(EReplayActions.DeselectDayFromMultidayPanel),
      switchMap(() => [new EndReplayPlotAnimation(), new ViewAll()])
    )
  );

  public onMapPolylineClicked$ = createEffect(() =>
    this._actions$.pipe(
      ofType<PolylineClicked>(EMapsActions.PolylineClicked),
      map(action => action.payload),
      map(polylineInfo => new SelectDayFromMultidayPanel(polylineInfo.id))
    )
  );

  public onSelectedSegmentChangesCloseBalloon$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SelectReplaySegmentFromTimeline | ViewAll>(EReplayActions.SelectReplaySegmentFromTimeline, EReplayActions.ViewAll),
      withLatestFrom(this._store.pipe(select(getBalloon))),
      filter(([, balloon]) => !isElementNull(balloon)),
      map(() => new CloseBalloon())
    )
  );

  public showPolylineHoverBalloon$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<PolylineStartHover>(EMapsActions.PolylineStartHover),
      map(action => action.payload),
      map(polylineHoverParams => new GetPolylineBalloon(polylineHoverParams))
    )
  );

  public closePolylineHoverBalloon$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<PolylineEndHover>(EMapsActions.PolylineEndHover),
      map(() => new CloseBalloon())
    )
  );

  public onPanelScrolled$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<PanelScrolled>(EReplayActions.PanelScrolled),
        map(action => action.payload),
        tap(scrollEvent => this._pendoService.trackEvent(scrollEvent, {}))
      ),
    { dispatch: false }
  );

  public onNextPlotInJourney$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<NextPlotInJourney>(EReplayActions.NextPlotInJourney),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getReplayPlots))),
      switchMap(([replayPlotId, plots]) => {
        const currentPlotIndex = plots.findIndex(p => p.id === replayPlotId);
        if (currentPlotIndex >= plots.length - 1) {
          return EMPTY;
        }

        const nextPlot = plots[currentPlotIndex + 1];

        const actions: Action[] = this._getBalloonActions(nextPlot);
        return actions;
      })
    )
  );

  public onPreviousPlotInJourney$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<PreviousPlotInJourney>(EReplayActions.PreviousPlotInJourney),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getReplayPlots))),
      switchMap(([replayPlotId, plots]) => {
        const currentPlotIndex = plots.findIndex(p => p.id === replayPlotId);
        if (currentPlotIndex === 0) {
          return EMPTY;
        }

        const nextPlot = plots[currentPlotIndex - 1];

        const actions: Action[] = this._getBalloonActions(nextPlot);
        return actions;
      })
    )
  );

  public onRequestVideoJourney$ = createEffect(() =>
    this._actions$.pipe(
      ofType<RequestVideoInJourney>(EReplayActions.RequestVideoInJourney),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getReplayJourney))),
      switchMap(([actionPayload, journey]) =>
        this._videoEventHttpService
          .postVodEvents(actionPayload.startTimeUtc, actionPayload.endTimeUtc, journey.id)
          .pipe(map(() => new RequestVideoInJourneySuccess()))
      )
    )
  );

  public onOpenBalloonSelectSegment$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayBalloonSuccess>(EBalloonInfoActions.GetReplayBalloonSuccess),
      withLatestFrom(this._windowResizeService.isMobileOrTablet$),
      filter(([, isMobileOrTablet]) => isMobileOrTablet),
      map(([action]) => action.payload.id),
      switchMap(id => this._store.pipe(select(getSegmentOfPlot, { plotId: id }), take(1))),
      map(segment => new SelectReplaySegmentFromMap(segment.id))
    )
  );

  public onOpenBalloonZoomToPlot$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayBalloonSuccess>(EBalloonInfoActions.GetReplayBalloonSuccess),
      withLatestFrom(this._windowResizeService.isMobileOrTablet$),
      filter(([, isMobileOrTablet]) => isMobileOrTablet),
      map(([action]) => action.payload.latLng),
      map(latLng => new ChangeMapBounds([latLng]))
    )
  );

  public onCloesBalloonDeselectReplaySegment$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseBalloon>(EBalloonInfoActions.CloseBalloon),
      withLatestFrom(this._windowResizeService.isMobileOrTablet$, this._store.pipe(select(getIsSelectedSegmentSticky))),
      filter(([, isMobileOrTablet]) => isMobileOrTablet),
      filter(([, , isSticky]) => !isSticky),
      map(() => new DeselectReplaySegment())
    )
  );

  public onMobileIneractionsCloseDrawer$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayBalloonSuccess | SelectReplaySegmentFromTimeline | ViewAll | GetPlaceBalloonSuccess>(
        EBalloonInfoActions.GetReplayBalloonSuccess,
        EReplayActions.SelectReplaySegmentFromTimeline,
        EReplayActions.ViewAll,
        EBalloonInfoActions.GetPlaceBalloonSuccess
      ),
      withLatestFrom(this._windowResizeService.isMobileOrTablet$),
      filter(([, isMobileOrTablet]) => isMobileOrTablet),
      map(() => new UpdateMobileDrawerState(EPanelDrawerState.Closed))
    )
  );

  public getJourneyForNewVehicle$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetNewReplayJourney>(EReplayActions.GetNewReplayJourney),
      map(action => action.payload),
      concatMap(journeyParams => {
        // Select vehicle so plot is on the map
        // Update the replay layout vehicle id
        // Get new journey data
        const actions: Action[] = [];
        actions.push(
          new UpdateVehicleAsSelected({
            focus: false,
            selectedEntity: `v${journeyParams.id}`
          })
        );
        actions.push(new UpdateViewReplayIdAndType({ id: journeyParams.id, type: journeyParams.type }));
        actions.push(new GetReplayJourney(journeyParams));
        return actions;
      })
    )
  );

  public onVehicleUpdateGetJourneyUpdates$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateVehicles>(EVehicleActions.UpdateVehicles),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getReplayJourney)), this._store.pipe(select(getUserSettings))),
      filter(
        ([vehicleUpdate, journey, userSettings]) =>
          // if the vehicle update matches the vehicle that is replayed
          // AND the replay date range contains the current day
          vehicleUpdate.id === journey?.id &&
          journey?.selectedDateRange.contains(this._getTimeZoneAdjustedTime(vehicleUpdate, userSettings.TimeZoneAndroidId)) &&
          journey?.days[0].segments.length > 0
      ),
      map(([vehicleUpdate]) => new AddLivePlotToJourney(vehicleUpdate))
    )
  );

  public onAddLivePlotToJourney$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<AddLivePlotToJourney>(EReplayActions.AddLivePlotToJourney),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getReplayJourney))),
      switchMap(([vehiclePlotResponse, replayJourney]) => {
        const journey = ReplayMappingUtilities.addLivePlotToJourney(replayJourney, vehiclePlotResponse);
        return [new AddLivePlotToJourneySuccess(journey), new GetJourneyUpdates()];
      })
    )
  );

  public onGetJourneyUpdatesForVehicle$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetJourneyUpdates>(EReplayActions.GetJourneyUpdates),
      withLatestFrom(this._store.pipe(select(getReplayJourney)), this._store.pipe(select(getReplayLayout))),
      filter(([, journey]) => !isElementNull(journey) && journey.days[0].segments.length > 0),
      delay(GetJourneyUpdatesDelayMs),
      /*
     we are making calls to the APIs so throttle the requests according to a congfiguration value.
     by default throttleTime will let the first request through, then ignore all subsequent requests during the throttleTime period
     this configuration { leading: true, trailing: true } will ensure that if there were any requests during the throttleTime period,
     at the end of the period it will fire a request to the APIs
    */
      throttleTime(parseInt(environment.config.replayUpdateThrottleMs, 10), undefined, {
        leading: true,
        trailing: true
      }),
      map(([, journey, layout]) =>
        // get the data with time stamp >  the start time of the last segment
        this._getRequestParamsForJourneyUpdates(journey, layout)
      ),
      withLatestFrom<
        IGetReplayJourneyParams,
        [IGetReplayJourneyParams, IReplayUserSettings, number, IUserSettings, boolean, boolean, boolean, boolean]
      >(
        this._store.pipe(select(getReplayUserSettings)),
        this._store.pipe(select(getUserSpeedingThreshold)),
        this._store.pipe(select(getUserSettings)),
        this._store.pipe(select(getShowSpeedingEvents)),
        this._store.pipe(select(getShowHarshDrivingEvents)),
        this._store.pipe(select(getShowReplayVideoEnabled))
      ),
      switchMap(
        ([
          requestParams,
          replayUserSettings,
          userSpeedingThreshold,
          userSettings,
          showSpeedingEvents,
          showHarshDrivingEvents,
          showVideoEvents
        ]) => {
          const getData$ = this._getVehicleReplayData(
            requestParams,
            replayUserSettings,
            userSpeedingThreshold,
            userSettings,
            showSpeedingEvents,
            showHarshDrivingEvents,
            showVideoEvents
          ).pipe(
            share(),
            catchError(() => of<TGetReplayData>([null, null, null, null, null]))
          );

          const [callApisFailed$, callApisSucceeded$] = partition(
            getData$,
            ([journeyResponse, plotsResponse]) => isElementNull(journeyResponse) || isElementNull(plotsResponse)
          );

          const handleFailed$ = callApisFailed$.pipe(map(() => new GetReplayJourneyError()));
          const handleSucceeded$ = callApisSucceeded$.pipe(
            withLatestFrom(this._store.pipe(select(getReplayJourney)), this._store.pipe(select(getVehiclePlots))),
            filter(([, existingJourney]) => !isElementNull(existingJourney)),
            switchMap(([[journeyResponse, plotsResponse, params, driverNames], existingJourney, vehiclePlots]) => {
              const plot = vehiclePlots.find(p => p.id === params.id);

              const extendedJourney = ReplayMappingUtilities.extendJourney(
                journeyResponse,
                plotsResponse,
                plot,
                params,
                driverNames,
                existingJourney
              );

              return [new GetJourneyUpdatesSuccess(extendedJourney)];
            })
          );

          return merge(handleFailed$, handleSucceeded$);
        }
      )
    )
  );

  public onJourneyUpdatesFitToMap$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetJourneyUpdatesSuccess | AddLivePlotToJourneySuccess>(
        EReplayActions.GetJourneyUpdatesSuccess,
        EReplayActions.AddLivePlotToJourneySuccess
      ),
      withLatestFrom(this._store.pipe(select(canFitToMap))),
      filter(([, canFit]) => !canFit), // only disptach fit to map if fit to map is enabled
      map(() => new FitToMap())
    )
  );

  public onSegmentVideoClickedOpenBalloon$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SegmentVideoClicked>(EReplayActions.SegmentVideoClicked),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getReplayPlots))),
      map(([video, plots]) => plots.find(plot => plot.video?.key === video?.key)),
      switchMap(videoPlot => this._getBalloonActions(videoPlot))
    )
  );

  triggerVerifyRecentActivityDate$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenReplayOnLiveMap>(ELayoutActions.OpenReplayOnLiveMap),
      filter(() => isPlatformBrowser(this._platformId)),
      withLatestFrom(this._store.pipe(select(getVehiclePlots)), this._store.pipe(select(getAssetPlots))),
      map(([action, vehiclePlots, assetPlots]) => ({
        plot:
          action.payload.type === EViewReplayType.Vehicle
            ? vehiclePlots.find(p => p.id === action.payload.id)
            : assetPlots.find(p => p.id === action.payload.id),
        vehicleId: action.payload.id
      })),
      filter(({ plot }) => !isElementNull(plot)),
      map(
        ({ plot, vehicleId }) =>
          new VerifyRecentActivityDate({
            vehicleId,
            date: EpochTimeUtilities.fromEpochSecondsToDate(EpochTimeUtilities.fromDotNetTicksToSeconds(plot.ticks))
          })
      )
    )
  );

  verifyRecentActivityDate$ = createEffect(() =>
    this._actions$.pipe(
      ofType<VerifyRecentActivityDate>(EReplayActions.VerifyRecentActivityDate),
      switchMap(action => {
        const { vehicleId, date } = action.payload;
        const startTime = moment(date).startOf('day').toDate();
        const endTime = moment(date).endOf('day').toDate();

        return this._replayHttpService
          .getReplayJourneyForVehicle({
            vehicleId,
            startTime,
            endTime,
            speedThreshold: 0,
            minimumStopDuration: 0,
            minimumIdleDuration: 0,
            ignitionOnly: false,
            includePlaces: false
          })
          .pipe(
            map(response => {
              const hasData = response?.centricities?.[0]?.vehicles?.length > 0;
              return new VerifyRecentActivityDateSuccess(hasData ? date : null);
            }),
            catchError(() => of(new VerifyRecentActivityDateError()))
          );
      })
    )
  );

  constructor(
    private readonly _actions$: Actions,
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _replayHttpService: ReplayHttpService,
    private readonly _addressResolutionHttpService: AddressResolutionHttpService,
    private readonly _customerMetadataHttpService: CustomerMetadataHttpService,
    private readonly _store: Store<IAppState>,
    private readonly _pendoService: PendoService,
    private readonly _windowResizeService: WindowResizeService,
    private readonly _videoEventHttpService: VideoEventHttpService,
    private readonly _assetHttpService: AssetsHttpService
  ) {}

  private _handleVehicleReplayData(
    requestParams: IGetReplayJourneyParams,
    replayUserSettings: IReplayUserSettings,
    userSpeedingThreshold: number,
    userSettings: IUserSettings,
    showSpeedingEvents: boolean,
    showHarshDrivingEvents: boolean,
    showVideoEvents: boolean
  ): Observable<Action> {
    const getData$ = this._getVehicleReplayData(
      requestParams,
      replayUserSettings,
      userSpeedingThreshold,
      userSettings,
      showSpeedingEvents,
      showHarshDrivingEvents,
      showVideoEvents
    ).pipe(
      share(),
      catchError(() => of<TGetReplayData>([null, null, null, null, null]))
    );

    const [callApisFailed$, callApisSucceeded$] = partition(
      getData$,
      ([journeyResponse, plotsResponse]) => isElementNull(journeyResponse) || isElementNull(plotsResponse)
    );

    const handleFailed$ = callApisFailed$.pipe(map(() => new GetReplayJourneyError()));
    const handleSucceeded$ = callApisSucceeded$.pipe(
      withLatestFrom(this._store.pipe(select(getVehiclePlots))),
      switchMap(([[journeyResponse, plotsResponse, params, driverNames, videoEvents], vehiclePlots]) => {
        const plot = vehiclePlots.find(p => p.id === params.id);

        const journey = this._getVehicleJourneyOrDefaultIfNoData(journeyResponse, plotsResponse, plot, params, driverNames, videoEvents);
        let actions: Action[] = [new GetReplayJourneySuccess(journey)];
        actions = this._shouldPlayPlotAnimation(journey, actions);
        actions = this._shouldSelectASegmentFromTime(journey, params.selectedSegmentTime, actions);
        return actions;
      })
    );

    return merge(handleFailed$, handleSucceeded$);
  }

  private _handleAssetReplayData(params: IGetReplayJourneyParams, userSettings: IUserSettings) {
    const getData$ = this._getAssetReplayData(params, userSettings).pipe(share());
    return getData$.pipe(
      withLatestFrom(this._store.pipe(select(getAssetPlots))),
      filter(([, assetPlots]) => !isArrayNullOrEmpty(assetPlots)),
      take(1),
      switchMap(([journeyResponse, assetPlots]) => {
        let actions: Action[] = [];
        if (isElementNull(journeyResponse)) {
          actions.push(new GetReplayJourneyError());
        } else {
          const assetPlot = assetPlots.find(plot => plot.id === params.id);
          const locationHistoryModels = journeyResponse.locationsHistory.map(x => {
            const model: IAssetLocationsHistory = {
              address: x.address,
              latlng: new LatLng(x.coordinates.latitude, x.coordinates.longitude),
              eventCode: <ETrackingDeviceEventCode>x.eventCode,
              gpsQuality: <EGPSQuality>x.gpsQuality,
              gpsUpdateUtc: new Date(Date.parse(x.gpsUpdateUtc))
            };
            return model;
          });
          const journey = this._getAssetJourneyOrDefaultIfNoData(locationHistoryModels, params, assetPlot);
          actions.push(new GetReplayJourneySuccess(journey));
          actions = this._shouldPlayPlotAnimation(journey, actions);
          actions = this._shouldSelectASegmentFromTime(journey, params.selectedSegmentTime, actions);
        }

        return actions;
      })
    );
  }

  private _getRequestParamsForJourneyUpdates(journey: IReplayJourney, replayLayout: IReplayLayout): IGetReplayJourneyParams {
    const lastNonSyntheticSegment = journey.days
      .slice(-1)[0]
      .segments.filter(s => !s.isSynthetic)
      .slice(-1)[0];

    return {
      id: journey.id,
      dateRange: new DateRange(lastNonSyntheticSegment.dateRange.start.toDate(), journey.selectedDateRange.end),
      type: replayLayout.viewReplayParams.type,
      shouldQueryExtraDay: false
    };
  }

  private _getTimeZoneAdjustedTime(vehicleUpdate: IVehiclePlotResponse, timeZone: string) {
    const date = EpochTimeUtilities.fromDotNetTicksToDate(vehicleUpdate.ticks);
    return MomentUtilities.getTimeZoneAdjusted(date, timeZone);
  }

  private _getBalloonActions(plot: IReplayPlot): Action[] {
    const actions: Action[] = [];
    const latLng = new LatLng(plot.center.Latitude, plot.center.Longitude);
    actions.push(
      new GetReplayBalloon({
        data: {
          id: plot.id,
          latLng,
          type: EPlotType.replay,
          title: plot.name,
          options: {}
        }
      })
    );
    actions.push(new ChangeMapBounds([latLng]));
    return actions;
  }

  private _getTrackableName(
    vehiclePlots: IVehiclePlot[],
    assetPlots: IAssetPlot[],
    request: IGetReplayJourneyParams,
    journey: IReplayJourney
  ): string {
    const hasNameFromJourney = !isElementNull(journey) && !isStringNullOrEmpty(journey.name);
    if (hasNameFromJourney) {
      return journey.name;
    }

    const nameFromPlot =
      request.type === EViewReplayType.Vehicle
        ? vehiclePlots.find(p => p.id === request.id)?.vehicleName
        : assetPlots.find(p => p.id === request.id)?.name;
    return !isStringNullOrEmpty(nameFromPlot) ? nameFromPlot : '';
  }

  private _shouldPlayPlotAnimation(journey: IReplayJourney, actions: Action[]): Action[] {
    // animation should only play in a single day
    if (!journey.selectedDateRange.isMultiDay() && journey.days[0].segments.length > 0) {
      actions = [new StartReplayPlotAnimation(), ...actions];
    }
    return actions;
  }

  private _shouldSelectASegmentFromTime(journey: IReplayJourney, segmentSelectDate: Date, actions: Action[]): Action[] {
    if (isElementNull(segmentSelectDate)) {
      return actions;
    }

    const dayIndex = journey.days.findIndex(day => day.selectedDateRange.contains(segmentSelectDate));

    // If journey is multiday, open correct day
    // Find segment id, and select segment
    if (dayIndex >= 0) {
      const segmentId = journey.days[dayIndex].segments.find(segment => segment.dateRange.contains(segmentSelectDate))?.id;
      if (!isElementNull(segmentId)) {
        if (journey.days.length > 1) {
          actions.push(new SelectDayFromMultidayPanel(dayIndex + 1));
        }

        actions.push(new SelectReplaySegmentFromTimeline(segmentId));
      }
    }

    return actions;
  }

  private _getVehicleJourneyOrDefaultIfNoData(
    journeyResponse: IGetVehicleJourneyResponse,
    plotsResponse: IGetVehicleReplayPlotsResponse,
    plot: IVehiclePlot,
    params: IGetReplayJourneyParams,
    driverNames: IIndexableObject<string>,
    videoEvents: IVideoEvent[]
  ): IReplayJourney {
    let journey = ReplayMappingUtilities.mapVehicleJourney(journeyResponse, plotsResponse, plot, params, driverNames, videoEvents);

    if (isElementNull(journey)) {
      journey = ReplayMappingUtilities.getEmptyJourney(params.id, params.dateRange, plot?.vehicleName, EViewReplayType.Vehicle);
    }
    return journey;
  }

  private _getAssetJourneyOrDefaultIfNoData(
    locationHistory: IAssetLocationsHistory[],
    params: IGetReplayJourneyParams,
    assetPlot: IAssetPlot
  ): IReplayJourney {
    let journey = ReplayMappingUtilities.mapAssetJourney(locationHistory, params, assetPlot);

    if (isElementNull(journey)) {
      journey = ReplayMappingUtilities.getEmptyJourney(params.id, params.dateRange, assetPlot.name, EViewReplayType.Asset);
    }
    return journey;
  }

  private _getPlotResponse(getJourneyForVehicleParams: IGetReplayJourneyParams): Observable<IGetVehicleReplayPlotsResponse> | null {
    const plotsRequest: IGetReplayPlotsRequest = {
      vehicleId: getJourneyForVehicleParams.id,
      startTime: getJourneyForVehicleParams.dateRange.getUtcStart(),
      endTime: this._getEndDateFromJourneyForVehicleParams(getJourneyForVehicleParams)
    };

    const plotsResponse = this._replayHttpService.getReplayPlots(plotsRequest).pipe(delayedRetry());
    return plotsResponse;
  }

  private _getJourneyResponse(
    getJourneyForVehicleParams: IGetReplayJourneyParams,
    replayUserSettings: IReplayUserSettings,
    userSpeedingThreshold: number
  ): Observable<IGetVehicleJourneyResponse> | null {
    const journeyRequest = this._buildJourneyRequest(getJourneyForVehicleParams, replayUserSettings, userSpeedingThreshold);
    const journeyResponse = this._replayHttpService.getReplayJourneyForVehicle(journeyRequest).pipe(
      delayedRetry(),
      switchMap(response => this._reverseGeocodeVehicleJourneyResponse(response))
    );
    return journeyResponse;
  }

  private _buildJourneyRequest(
    getJourneyForVehicleParams: IGetReplayJourneyParams,
    replayUserSettings: IReplayUserSettings,
    userSpeedingThreshold: number
  ): IGetJourneyForVehicleRequest {
    /*
     * minimumIdleDuration defaults to the user setting
     * minimumStopDuration defaults to the user setting
     * ignitionOnly defaults to true
     */
    const minimumIdleDuration = isElementNull(getJourneyForVehicleParams.accountSettingOverrides?.minimumIdleDuration)
      ? replayUserSettings.minimumIdleDuration
      : getJourneyForVehicleParams.accountSettingOverrides.minimumIdleDuration;
    const minimumStopDuration = isElementNull(getJourneyForVehicleParams.accountSettingOverrides?.minimumStopDuration)
      ? replayUserSettings.minimumStopDuration
      : getJourneyForVehicleParams.accountSettingOverrides.minimumStopDuration;
    const ignitionOnly =
      getJourneyForVehicleParams.accountSettingOverrides?.showIdleSegments === null ||
      getJourneyForVehicleParams.accountSettingOverrides?.showIdleSegments === undefined
        ? true
        : getJourneyForVehicleParams.accountSettingOverrides.showIdleSegments;

    const request: IGetJourneyForVehicleRequest = {
      vehicleId: getJourneyForVehicleParams.id,
      startTime: getJourneyForVehicleParams.dateRange.getUtcStart(),
      endTime: this._getEndDateFromJourneyForVehicleParams(getJourneyForVehicleParams),
      minimumIdleDuration,
      minimumStopDuration,
      speedThreshold: userSpeedingThreshold,
      includePlaces: true,
      ignitionOnly
    };
    return request;
  }

  private _reverseGeocodeVehicleJourneyResponse(response: IGetVehicleJourneyResponse): Observable<IGetVehicleJourneyResponse> {
    const vehicleCentricity = response.centricities[0].vehicles[0];
    if (!isElementNull(vehicleCentricity) && this._vehicleCentricityHasSegments(vehicleCentricity)) {
      const segments = vehicleCentricity.centricities[0].segments;
      const reverseGeocodeParams: IReverseGeocodeRequest[] = this._getReverseGeocodeParamsFromSegments(vehicleCentricity.id, segments);
      return reverseGeocodeParams.length > 0
        ? this._addressResolutionHttpService.getReverseGeocode(reverseGeocodeParams).pipe(
            delayedRetry(),
            map(addresses => {
              response.centricities[0].vehicles[0].centricities[0].segments = this._mapAddressesToSegments(addresses, segments);
              return response;
            })
          )
        : of(response);
    }
    return of(response);
  }

  private _reverseGeocodeAssetJourneyResponse(
    response: IAssetLocationHistoryResponseDto,
    assetId: number
  ): Observable<IAssetLocationHistoryResponseDto> {
    const reverseGeocodeParams: IReverseGeocodeRequest[] = response?.locationsHistory
      ?.filter(locationHistory => isStringNullOrEmpty(locationHistory.address))
      ?.map(locationHistory => ({
        key: locationHistory.gpsUpdateUtc.toString(),
        data: {
          vid: assetId,
          Latitude: locationHistory.coordinates.latitude,
          Longitude: locationHistory.coordinates.longitude
        }
      }));
    return !isElementNull(reverseGeocodeParams) && reverseGeocodeParams.length > 0
      ? this._addressResolutionHttpService.getReverseGeocode(reverseGeocodeParams).pipe(
          delayedRetry(),
          map(addresses => {
            response.locationsHistory.forEach(location => {
              location.address =
                addresses.find(address => address.key === location.gpsUpdateUtc.toString())?.fulladdress ?? location.address;
            });
            return response;
          })
        )
      : of(response);
  }

  private _vehicleCentricityHasSegments(vehicleCentricity: Vehicle): boolean {
    return vehicleCentricity.centricities.length > 0 && vehicleCentricity.centricities[0].segments.length > 0;
  }

  private _getReverseGeocodeParamsFromSegments(vehicleId: number, segments: Segment[]): IReverseGeocodeRequest[] {
    const reverseGeocodeParams: IReverseGeocodeRequest[] = [];
    segments
      .filter(segment => this._shouldSegmentBeReverseGeocoded(segment))
      .forEach(segment => {
        reverseGeocodeParams.push({
          key: segment.start.time.utc.toString(),
          data: {
            vid: vehicleId,
            Latitude: segment.start.address.position.latitude,
            Longitude: segment.start.address.position.longitude
          }
        });
      });
    return reverseGeocodeParams;
  }

  private _shouldSegmentBeReverseGeocoded(segment: Segment): boolean {
    return isStringNullOrEmpty(segment.start.address.formattedAddress);
  }

  private _mapAddressesToSegments(addresses: IReverseGeocodeResponse[], segments: Segment[]): Segment[] {
    addresses.forEach(address => {
      segments.forEach(segment => {
        if (address.key === segment.start.time.utc.toString()) {
          segment.start.address.formattedAddress = address.fulladdress;
        }
      });
    });
    return segments;
  }

  private _adjustParamsToUserTimezone(requestParams: IGetReplayJourneyParams, userSettings: IUserSettings): IGetReplayJourneyParams {
    const adjustedRequestParams = {
      ...requestParams,
      dateRange: requestParams.dateRange.getDateRangeInTimezone(userSettings.TimeZoneAndroidId)
    };

    return adjustedRequestParams;
  }

  private _getVehicleReplayData(
    requestParams: IGetReplayJourneyParams,
    replayUserSettings: IReplayUserSettings,
    userSpeedingThreshold: number,
    userSettings: IUserSettings,
    showSpeedingEvents: boolean,
    showHarshDrivingEvents: boolean,
    showVideoEvents: boolean
  ): Observable<TGetReplayData> {
    /*
        get journey and plots adjusted for user time zone (not the machine time zone - user timezone is defined at the account setup)
        for example, if the user is in GMT-6:00 and current time is 10:00AM 15/1/2020.
        before the adjustment we would request:
        0:00:00 15/1/2020 (UTC)
        23:59:59 15/1/2020 (UTC)

        after the adjustment we would request:
        6:00:00 15/1/2020 (UTC)
        5:59:59 16/1/2020 (UTC)
      */
    const timezoneAdjustedParams = this._adjustParamsToUserTimezone(requestParams, userSettings);

    const journeyResponseObservable = this._getJourneyResponse(timezoneAdjustedParams, replayUserSettings, userSpeedingThreshold).pipe(
      map(journeyResponse =>
        this._removeHarshDrivingDataFromJourneyIfNoPermission(journeyResponse, showSpeedingEvents, showHarshDrivingEvents)
      ),
      switchMap(journeyResponse => this._getDriverNames(journeyResponse).pipe(map(driverNames => ({ journeyResponse, driverNames }))))
    );

    const plotsResponseObservable = this._getPlotResponse(timezoneAdjustedParams).pipe(
      map(plotsResponse => this._removeHarshDrivingDataFromPlotsIfNoPermission(plotsResponse, showSpeedingEvents, showHarshDrivingEvents))
    );

    const videoEventsResponseObservable = showVideoEvents
      ? this._videoEventHttpService
          .getVideoEvents(
            timezoneAdjustedParams.dateRange.start.toDate(),
            timezoneAdjustedParams.dateRange.end.toDate(),
            timezoneAdjustedParams.dateRange.differenceDays(),
            timezoneAdjustedParams.id
          )
          .pipe(catchError(() => of([])))
      : of([]);

    return forkJoin([journeyResponseObservable, plotsResponseObservable, of(timezoneAdjustedParams), videoEventsResponseObservable]).pipe(
      map(([joureyAndDrivers, plotResponse, params, videoEvents]) => [
        joureyAndDrivers.journeyResponse,
        plotResponse,
        params,
        joureyAndDrivers.driverNames,
        videoEvents
      ])
    );
  }

  private _getAssetReplayData(
    requestParams: IGetReplayJourneyParams,
    userSettings: IUserSettings
  ): Observable<IAssetLocationHistoryResponseDto> {
    return this._store.pipe(
      select(getAssetDetails),
      map(assetDetails => assetDetails.find(asset => asset.id === requestParams.id)),
      switchMap(asset => {
        const timezoneAdjustedParams = this._adjustParamsToUserTimezone(requestParams, userSettings);
        const numberOfRows = timezoneAdjustedParams.dateRange.differenceDays() * 1000;
        const historyRequest: IAssetLocationHisoryRequestDto = {
          assetTrackingId: asset.vehicleTrackingId,
          dbId: asset.dbId,
          numberOfRows,
          offset: 0,
          startFromDaysAgo: 0,
          startDate: timezoneAdjustedParams.dateRange.start.toDate(),
          endDate: timezoneAdjustedParams.dateRange.end.toDate()
        };
        return this._assetHttpService.getLocationHistory(historyRequest).pipe(
          delayedRetry(),
          switchMap(response => this._reverseGeocodeAssetJourneyResponse(response, asset.id))
        );
      })
    );
  }

  private _getDriverNames(journeyResponse: IGetVehicleJourneyResponse): Observable<IIndexableObject<string>> {
    const driverIds = ReplayMappingUtilities.getDriverIds(journeyResponse);

    if (isElementNull(driverIds) || driverIds.length === 0) {
      return of({});
    }

    const getDriverDetails$: Observable<IDriverDetails>[] = driverIds.map(driverId =>
      this._driverNamesMap.has(driverId)
        ? of(this._driverNamesMap.get(driverId))
        : this._customerMetadataHttpService.getDriverDetails(driverId).pipe(
            delayedRetry(),
            tap(driverDetails => this._driverNamesMap.set(driverId, driverDetails)),
            catchError(() => of(null))
          )
    );

    return forkJoin([...getDriverDetails$]).pipe(
      switchMap((driverDetails: IDriverDetails[]) => {
        const driverNames: IIndexableObject<string> = {};
        driverDetails
          .filter(driver => !isElementNull(driver))
          .forEach(driver => {
            driverNames[driver.DriverId] = `${driver.FirstName} ${driver.LastName}`;
          });

        return of(driverNames);
      })
    );
  }

  private _removeHarshDrivingDataFromJourneyIfNoPermission(
    journeyResponse: IGetVehicleJourneyResponse,
    showSpeedingEvents: boolean,
    showHarshDrivingEvents: boolean
  ): IGetVehicleJourneyResponse {
    if (ReplayMappingUtilities.journeyHasNoDataForVehicle(journeyResponse)) {
      return journeyResponse;
    }

    const segments = journeyResponse.centricities[0].vehicles[0].centricities[0].segments.map(segment => {
      if (!showSpeedingEvents) {
        segment.harshDrivingEvents.highSpeedEvents = 0;
      }

      if (!showHarshDrivingEvents) {
        segment.harshDrivingEvents.moderateAccelerationEvents = 0;
        segment.harshDrivingEvents.moderateBrakingEvents = 0;
        segment.harshDrivingEvents.moderateLeftCorneringEvents = 0;
        segment.harshDrivingEvents.moderateRightCorneringEvents = 0;
        segment.harshDrivingEvents.severeAccelerationEvents = 0;
        segment.harshDrivingEvents.severeBrakingEvents = 0;
        segment.harshDrivingEvents.severeLeftCorneringEvents = 0;
        segment.harshDrivingEvents.severeRightCorneringEvents = 0;
      }

      return segment;
    });

    journeyResponse.centricities[0].vehicles[0].centricities[0].segments = segments;

    return journeyResponse;
  }

  private _removeHarshDrivingDataFromPlotsIfNoPermission(
    plotsResponse: IGetVehicleReplayPlotsResponse,
    showSpeedingEvents: boolean,
    showHarshDrivingEvents: boolean
  ): IGetVehicleReplayPlotsResponse {
    if (plotsResponse.plots && plotsResponse.plots.length > 0) {
      plotsResponse.plots = plotsResponse.plots.map(plot => {
        if (plot.postedSpeedLimit && !showSpeedingEvents) {
          plot.postedSpeedLimit = null;
        }

        if (plot.accelerationSeverity && !showHarshDrivingEvents) {
          plot.accelerationSeverity = null;
        }

        return plot;
      });
    }

    return plotsResponse;
  }

  private _getEndDateFromJourneyForVehicleParams(getJourneyForVehicleParams: IGetReplayJourneyParams) {
    const daysToAdd = getJourneyForVehicleParams.shouldQueryExtraDay ? 1 : 0;
    const endDate = getJourneyForVehicleParams.dateRange.end.clone().add(daysToAdd, 'days');
    const requestEndDate = MomentUtilities.getUtcDateFromMoment(endDate);
    return requestEndDate;
  }
}
